import DarkModeOutlined from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlined from '@mui/icons-material/LightModeOutlined';
import LogoutRounded from '@mui/icons-material/LogoutRounded';
import MenuRounded from '@mui/icons-material/MenuRounded';
import MenuOpenRounded from '@mui/icons-material/MenuOpenRounded';
import TranslateOutlined from '@mui/icons-material/TranslateOutlined';
import {
  AppBar,
  Avatar,
  Box,
  ButtonBase,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import { useColorScheme } from '@mui/material/styles';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth, useAuthActions } from '../auth/AuthProvider';
import type { AuthEnvironment } from '../auth/types';
import { supportedLanguages, type SupportedLanguage } from '../i18n';

export interface TopbarProps {
  readonly collapsed: boolean;
  readonly environment: AuthEnvironment;
  readonly mobile: boolean;
  readonly onNavigationToggle: () => void;
}

export function Topbar({ collapsed, environment, mobile, onNavigationToggle }: TopbarProps) {
  const auth = useAuth();
  const { signOut } = useAuthActions();
  const { t } = useTranslation();
  const displayName =
    auth.status === 'authenticated' ? auth.principal.actor.displayName : t('auth.profile');
  const email = auth.status === 'authenticated' ? auth.principal.email : null;
  const environmentBadge = environment.environment.toUpperCase();
  const environmentColors =
    environment.environment === 'prod'
      ? { background: 'critical.light', foreground: 'critical.dark' }
      : environment.environment === 'uat'
        ? { background: 'warning.light', foreground: 'warning.dark' }
        : { background: 'iconTint.blue.background', foreground: 'iconTint.blue.foreground' };
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const menuOpen = menuAnchor !== null;
  const closeMenu = () => setMenuAnchor(null);
  const handleSignOut = () => {
    closeMenu();
    void signOut();
  };

  return (
    <AppBar
      color="inherit"
      component="header"
      elevation={0}
      position="sticky"
      sx={{ borderBottom: 1, borderColor: 'divider' }}
    >
      <Toolbar
        sx={{
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          gap: 1.5,
          minHeight: '64px !important',
          px: { xs: 1.5, sm: 2.5 },
          py: { xs: 1, md: 0 },
        }}
      >
        <IconButton
          aria-label={
            mobile
              ? t('shell.openNavigation')
              : collapsed
                ? t('shell.expandNavigation')
                : t('shell.collapseNavigation')
          }
          edge="start"
          onClick={onNavigationToggle}
        >
          {mobile || collapsed ? <MenuRounded /> : <MenuOpenRounded />}
        </IconButton>

        <Box sx={{ flex: 1, minWidth: 0 }} />

        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'center',
            justifyContent: { xs: 'flex-end', md: 'initial' },
            order: { xs: 3, md: 2 },
            width: { xs: '100%', md: 'auto' },
          }}
        >
          <Chip
            label={environmentBadge}
            size="small"
            sx={{
              bgcolor: environmentColors.background,
              border: '1px solid',
              borderColor: environmentColors.foreground,
              color: 'text.primary',
              fontWeight: 800,
              letterSpacing: '0.04em',
            }}
          />
          {environment.authMode === 'mock' ? (
            <Chip
              label={t('auth.mockMode')}
              size="small"
              sx={{ bgcolor: 'mockData.light', color: 'mockData.dark' }}
            />
          ) : null}
          <LanguageMenu />
          <ColorSchemeToggle />
          <Typography sx={{ display: { xs: 'none', sm: 'block' } }} variant="body2">
            Asia/Bangkok
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', order: { xs: 2, md: 3 } }}>
          {auth.status === 'authenticated' ? (
            <>
              <ButtonBase
                aria-controls={menuOpen ? 'account-menu' : undefined}
                aria-expanded={menuOpen}
                aria-haspopup="menu"
                aria-label={t('auth.accountMenu')}
                onClick={(event) => setMenuAnchor(event.currentTarget)}
                sx={{
                  alignItems: 'center',
                  borderRadius: 999,
                  display: 'flex',
                  gap: 1,
                  px: 0.5,
                  py: 0.5,
                }}
              >
                <Avatar
                  aria-label={displayName}
                  role="img"
                  sx={{ bgcolor: 'primary.main', height: 40, width: 40 }}
                >
                  {initials(displayName)}
                </Avatar>
              </ButtonBase>
              <Menu
                anchorEl={menuAnchor}
                id="account-menu"
                onClose={closeMenu}
                open={menuOpen}
                slotProps={{
                  paper: { sx: { maxWidth: 'calc(100vw - 16px)', mt: 1, width: 320 } },
                }}
              >
                <Box sx={{ px: 2.25, py: 1.75 }}>
                  <Typography sx={{ fontSize: '1rem', fontWeight: 800, overflowWrap: 'anywhere' }}>
                    {displayName}
                  </Typography>
                  {email ? (
                    <Typography
                      color="text.secondary"
                      sx={{ mt: 0.25, overflowWrap: 'anywhere' }}
                      variant="body2"
                    >
                      {email}
                    </Typography>
                  ) : null}
                </Box>
                <Divider />
                <MenuItem onClick={handleSignOut} sx={{ minHeight: 48 }}>
                  <ListItemIcon>
                    <LogoutRounded fontSize="small" />
                  </ListItemIcon>
                  {t('auth.logout')}
                </MenuItem>
              </Menu>
            </>
          ) : (
            <>
              <Avatar
                aria-label={displayName}
                role="img"
                sx={{ bgcolor: 'primary.main', height: 40, width: 40 }}
              >
                {initials(displayName)}
              </Avatar>
            </>
          )}
        </Stack>
      </Toolbar>
    </AppBar>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/u)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}

function LanguageMenu() {
  const { i18n, t } = useTranslation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const active = (i18n.resolvedLanguage ?? i18n.language).split('-', 1)[0];

  return (
    <>
      <IconButton
        aria-controls={anchor === null ? undefined : 'language-menu'}
        aria-haspopup="menu"
        aria-label={t('shell.language')}
        onClick={(event) => setAnchor(event.currentTarget)}
        size="small"
      >
        <TranslateOutlined fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchor}
        id="language-menu"
        onClose={() => setAnchor(null)}
        open={anchor !== null}
      >
        {supportedLanguages.map((language: SupportedLanguage) => (
          <MenuItem
            aria-current={language === active ? 'true' : undefined}
            key={language}
            onClick={() => {
              setAnchor(null);
              void i18n.changeLanguage(language);
            }}
            selected={language === active}
          >
            {t(`languages.${language}`)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

function ColorSchemeToggle() {
  const { mode, setMode } = useColorScheme();
  const { t } = useTranslation();
  const dark = mode === 'dark';

  return (
    <IconButton
      aria-label={dark ? t('shell.lightMode') : t('shell.darkMode')}
      onClick={() => setMode(dark ? 'light' : 'dark')}
      size="small"
    >
      {dark ? <LightModeOutlined fontSize="small" /> : <DarkModeOutlined fontSize="small" />}
    </IconButton>
  );
}
